import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Mascot } from '@/components/Mascot';
import { Icon, Star } from '@/components/Icons';
import { getUtm, track } from '@/lib/analytics';
import { api } from '@/lib/api';
import { configured } from '@/lib/supabase';
import { messageFor } from '@/lib/errors';

const STEPS = [
  { n: 1, t: 'Реєструєтеся за хвилину', d: 'Батьки створюють акаунт і профіль дитини. Без дзвінків і очікування.' },
  { n: 2, t: 'Дитина вчиться з Єнотиком', d: 'Короткі уроки по 10–15 хвилин: пояснення, анімації, вправи з миттєвою підказкою.' },
  { n: 3, t: 'Ви бачите прогрес', d: 'Пройдені уроки, активність по днях і результат перевірки блоку — у кабінеті батьків.' },
];
const LESSONS = ['Числа і розряди', 'Порівняння чисел', 'Додавання', 'Віднімання', 'Додавання + віднімання'];

function Cta({ where, className = '' }: { where: string; className?: string }) {
  return <Link to="/register" className={`btn ${className}`} onClick={() => track('cta_click', { cta: where })}>Почати безкоштовно <Icon name="right" size={18} /></Link>;
}

function Demo() {
  const [pick, setPick] = useState<string | null>(null);
  const opts = ['6', '60', '600'];
  const ok = pick === '600';
  return (
    <div className="card flex flex-col gap-4">
      <span className="task-label">Спробуйте прямо тут</span>
      <h3 className="prompt">Що означає цифра 6 у числі 4&nbsp;682?</h3>
      <div className="opts" role="radiogroup" aria-label="Варіанти відповіді">
        {opts.map((o) => (
          <button key={o} role="radio" aria-checked={pick === o} className={`opt ${pick === o ? (ok ? 'good' : 'wrong') : ''}`} onClick={() => setPick(o)}>{o}</button>
        ))}
      </div>
      {pick && (
        <div className={`panel ${ok ? 'ok' : 'bad'}`} role="status">
          <Mascot pose={ok ? 'head' : 'think'} className={ok ? 'pm-head' : 'pm'} />
          <h3>{ok ? <><Icon name="check" size={24} /> Влучно!</> : <><Icon name="x" size={24} /> Майже!</>}</h3>
          <p className="font-bold">{ok ? 'Шістка стоїть у кімнаті сотень, тому вона означає 600.' : `Цифра 6 у числі 4 682 стоїть у сотнях, тому вона означає 600, а не ${pick}.`}</p>
        </div>
      )}
    </div>
  );
}

function LeadForm() {
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [err, setErr] = useState('');
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    if (!f.get('consent')) { setErr('Потрібна згода на обробку даних.'); setState('error'); return; }
    setState('sending');
    try {
      await api.captureLead({ email: f.get('email'), name: f.get('name'), phone: f.get('phone'), wantsCall: f.get('call') === 'on',
        consent: true, consentVersion: import.meta.env.VITE_CONSENT_VERSION ?? 'draft', website: f.get('website'), utm: getUtm() });
      setState('done');
    } catch (x) { setErr(messageFor(x)); setState('error'); }
  }
  if (state === 'done') return (
    <div className="card text-center" role="status">
      <h3 className="text-xl font-black">Дякуємо! Заявку отримано.</h3>
      <p className="mt-2 font-semibold text-ink-muted">Не чекайте дзвінка — можна почати вже зараз.</p>
      <div className="mt-4"><Cta where="lead_done" /></div>
    </div>
  );
  return (
    <form className="card flex flex-col gap-4" onSubmit={submit} noValidate={false}>
      <h3 className="text-xl font-black">Маєте питання? Залиште контакт</h3>
      <p className="-mt-2 font-semibold text-ink-muted">Ми напишемо або зателефонуємо. Реєстрація від цього не залежить.</p>
      <label><span className="label">Email</span><input className="input" name="email" type="email" required autoComplete="email" /></label>
      <div className="grid gap-4 sm:grid-cols-2">
        <label><span className="label">Ім’я</span><input className="input" name="name" autoComplete="given-name" maxLength={60} /></label>
        <label><span className="label">Телефон (необов’язково)</span><input className="input" name="phone" type="tel" autoComplete="tel" maxLength={20} /></label>
      </div>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      <label className="flex items-start gap-3 font-semibold"><input type="checkbox" name="call" className="mt-1 h-5 w-5" /> Хочу консультацію менеджера</label>
      <label className="flex items-start gap-3 font-semibold"><input type="checkbox" name="consent" className="mt-1 h-5 w-5" required />
        <span>Погоджуюся на обробку контактних даних згідно з <Link className="text-brand underline" to="/privacy">політикою приватності</Link></span></label>
      {state === 'error' && <p className="field-error" role="alert">{err}</p>}
      <button className="btn btn-ghost" disabled={state === 'sending' || !configured}>{state === 'sending' ? 'Надсилаємо…' : 'Надіслати заявку'}</button>
    </form>
  );
}

export default function Landing() {
  useEffect(() => { if (configured) track('landing_view', { page: 'landing' }); }, []);
  return (
    <div className="overflow-x-hidden">
      <header className="mx-auto flex max-w-[1140px] items-center gap-4 px-5 py-4">
        <div className="mr-auto"><Link to="/" className="block text-xl font-black leading-[.92]"><span className="block">MATH</span><span className="block text-brand">ZERO</span></Link></div>
        <Link to="/login" className="font-extrabold text-brand">Увійти</Link>
        <Cta where="header" className="btn-sm hidden sm:inline-flex" />
      </header>

      <section className="mx-auto grid max-w-[1140px] items-center gap-6 px-5 pb-10 pt-4 md:grid-cols-[1.1fr_.9fr] md:pt-10">
        <div className="flex flex-col items-start gap-5">
          <span className="tag">Для дітей 9–12 років · від ZNOHUB</span>
          <h1 className="text-[clamp(34px,5.4vw,58px)] font-black leading-[1.05]">Математика з&nbsp;нуля — <span className="text-brand">з&nbsp;Єнотиком</span></h1>
          <p className="max-w-xl text-lg font-semibold text-ink-muted">Короткі інтерактивні уроки, де дитина розуміє, а не зазубрює. Помилятися можна — Єнотик пояснить, що саме підтягнути.</p>
          <div className="flex flex-wrap items-center gap-3"><Cta where="hero" /><a href="#lead" className="btn btn-ghost">Залишити заявку</a></div>
          <p className="text-sm font-bold text-ink-muted">Перший блок — безкоштовно під час пілоту.</p>
        </div>
        <div className="relative mx-auto flex w-full max-w-[420px] justify-center">
          <div className="absolute inset-x-6 bottom-2 top-16 -z-10 rounded-[48px] bg-brand-soft" />
          <div className="bubble absolute left-0 top-6 z-10 max-w-[200px]">Привіт! Розберемося з числами разом? 🚀</div>
          <Mascot pose="point" className="h-[380px] w-auto md:h-[440px]" float enter />
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="mx-auto max-w-[1140px] px-5">
          <h2 className="text-3xl font-black">Як це працює</h2>
          <div className="mt-6 grid gap-5 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-3xl bg-page p-6">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand text-xl font-black text-white">{s.n}</span>
                <h3 className="mt-4 text-xl font-black">{s.t}</h3><p className="mt-2 font-semibold text-ink-muted">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-[1140px] items-center gap-8 px-5 py-14 md:grid-cols-2">
        <div>
          <h2 className="text-3xl font-black">Ось як виглядає вправа</h2>
          <p className="mt-3 text-lg font-semibold text-ink-muted">Після кожної відповіді дитина бачить, <b>чому</b> так. Неправильна відповідь — не покарання, а підказка.</p>
          <ul className="mt-5 space-y-3 font-bold">
            <li className="flex gap-3"><Star /> XP, серії днів і бейджі за справжній прогрес</li>
            <li className="flex gap-3"><Icon name="shield" className="text-ok" /> Без реклами й публічних рейтингів</li>
            <li className="flex gap-3"><Icon name="clock" className="text-brand" /> 10–15 хвилин на урок</li>
          </ul>
        </div>
        <Demo />
      </section>

      <section className="bg-white py-14">
        <div className="mx-auto grid max-w-[1140px] items-center gap-8 px-5 md:grid-cols-[1fr_auto]">
          <div>
            <h2 className="text-3xl font-black">Перший блок: «Числа та обчислення»</h2>
            <p className="mt-2 font-semibold text-ink-muted">5 уроків і перевірка «Перевір себе». Далі — ще 5 блоків курсу.</p>
            <ol className="mt-6 grid gap-3 sm:grid-cols-2">
              {LESSONS.map((t, i) => (
                <li key={t} className="flex items-center gap-3 rounded-2xl bg-page p-3.5 font-extrabold">
                  <span className="grid h-10 w-10 flex-none place-items-center rounded-full bg-brand text-white">{i + 1}</span>{t}
                </li>
              ))}
              <li className="flex items-center gap-3 rounded-2xl bg-xp-soft p-3.5 font-extrabold"><span className="grid h-10 w-10 flex-none place-items-center rounded-full bg-xp"><Icon name="star" /></span>Перевір себе</li>
            </ol>
          </div>
          <Mascot pose="proud" className="mx-auto h-72 w-auto" />
        </div>
      </section>

      <section id="lead" className="mx-auto grid max-w-[1140px] gap-8 px-5 py-14 md:grid-cols-2">
        <div className="flex flex-col items-start gap-4">
          <Mascot pose="think" className="h-56 w-auto" />
          <h2 className="text-3xl font-black">Готові спробувати?</h2>
          <p className="font-semibold text-ink-muted">Реєстрація займає хвилину. Дитячий профіль створюєте ви — без email дитини.</p>
          <Cta where="final" />
        </div>
        <LeadForm />
      </section>

      <footer className="border-t border-line py-8 text-center text-sm font-semibold text-ink-muted">
        © ZNOHUB · Math Zero · <Link to="/privacy" className="underline">Політика приватності</Link> · <Link to="/terms" className="underline">Умови</Link>
      </footer>
    </div>
  );
}
